import { Form } from '@unform/web';
import { CompactSelect, FormPageHeader, Paginator } from 'components/Shared';
import type { SelectOption } from 'contracts/Common';
import {
  ConfirmationDialog,
  ConfirmationDialogRef,
} from 'components/Shared/ConfirmationDialog';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  BulkDeletePhysicalDocksActions as BulkDeleteActions,
  PaginatePhysicalDocksActions as PaginateActions,
} from 'store/ducks/physicalDocks';
import PhysicalDockCreationModal, {
  Ref as CreationModalRef,
} from './CreationModal';
import PhysicalDocksList from './PhysicalDocksList';
import * as S from './styles';
import PhysicalDockUpdateModal, { Ref as UpdateModalRef } from './UpdateModal';

interface Props {
  warehouseId: number | string;
}

const PAGE_SIZE = 10;

export const PhysicalDocksManager: React.FC<Props> = ({ warehouseId }) => {
  const dispatch: AppDispatch = useDispatch();
  const creationModalRef = useRef<CreationModalRef>(null);
  const updateModalRef = useRef<UpdateModalRef>(null);
  const confirmationDialogRef = useRef<ConfirmationDialogRef>(null);
  const previousWarehouseIdRef = useRef(warehouseId);
  const { data, loading, errorMessage } = useSelector(
    (state: RootState) => state.paginatePhysicalDocks
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [buildingId, setBuildingId] = useState<number | null>(null);
  const [selectedDockIds, setSelectedDockIds] = useState<Set<number>>(
    () => new Set()
  );
  const [bulkLoading, setBulkLoading] = useState(false);
  const [query, setQuery] = useState({
    search: '',
    page: 1,
    limit: 99,
    warehouseId,
    orderBy: 'building',
    direction: 'asc',
  });

  const buildingOptions = useMemo(() => {
    const options = new Map<number, SelectOption>();
    data.forEach(({ building }) => {
      if (!building) return;
      options.set(building.id, {
        value: building.id,
        label: `${building.area.name} / ${building.name}`,
      });
    });
    return [...options.values()].sort((left, right) =>
      left.label.localeCompare(right.label, 'pt-BR')
    );
  }, [data]);
  const filteredDocks = useMemo(
    () =>
      buildingId === null
        ? data
        : data.filter((dock) => dock.buildingId === buildingId),
    [buildingId, data]
  );
  const localPagination = useMemo(
    () => ({
      currentPage,
      lastPage: Math.ceil(filteredDocks.length / PAGE_SIZE),
      total: filteredDocks.length,
    }),
    [currentPage, filteredDocks.length]
  );
  const visibleDocks = useMemo(
    () =>
      filteredDocks.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
      ),
    [currentPage, filteredDocks]
  );

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const onBuildingChange = useCallback((option: SelectOption | null) => {
    setBuildingId(option ? Number(option.value) : null);
    setCurrentPage(1);
  }, []);

  const onQueryChange = useCallback(() => {
    dispatch(PaginateActions.request(query));
  }, [dispatch, query]);

  const onToggleDock = useCallback((dockId: number) => {
    setSelectedDockIds((current) => {
      const next = new Set(current);
      next.has(dockId) ? next.delete(dockId) : next.add(dockId);
      return next;
    });
  }, []);

  const onToggleVisible = useCallback(
    (dockIds: number[], selected: boolean) => {
      setSelectedDockIds((current) => {
        const next = new Set(current);
        dockIds.forEach((id) => (selected ? next.add(id) : next.delete(id)));
        return next;
      });
    },
    []
  );

  const onDeleteSelected = useCallback(async () => {
    if (!selectedDockIds.size || bulkLoading) return;
    const selectedCount = selectedDockIds.size;
    const payload = {
      warehouseId: Number(warehouseId),
      dockIds: [...selectedDockIds].sort((left, right) => left - right),
    };
    const confirmed = await confirmationDialogRef.current?.openDialog({
      title: `Excluir ${selectedCount} ${
        selectedCount === 1 ? 'doca física' : 'docas físicas'
      }?`,
      message:
        selectedCount === 1
          ? 'A doca selecionada deixará de estar disponível para novos agendamentos e operações.'
          : 'As docas selecionadas deixarão de estar disponíveis para novos agendamentos e operações.',
      confirmButtonText: 'Excluir selecionadas',
      confirmButtonMood: 'danger',
    });
    if (!confirmed) return;

    setBulkLoading(true);
    dispatch(
      BulkDeleteActions.deleteRequest(
        payload,
        () => {
          setBulkLoading(false);
          setSelectedDockIds(new Set());
          setCurrentPage(1);
          onQueryChange();
        },
        () => setBulkLoading(false)
      )
    );
  }, [bulkLoading, dispatch, onQueryChange, selectedDockIds, warehouseId]);

  useEffect(() => {
    if (previousWarehouseIdRef.current === warehouseId) return;

    previousWarehouseIdRef.current = warehouseId;
    dispatch(PaginateActions.reset());
    setCurrentPage(1);
    setBuildingId(null);
    setSelectedDockIds(new Set());
    setQuery((state) => ({ ...state, warehouseId, page: 1 }));
  }, [dispatch, warehouseId]);

  useEffect(() => {
    onQueryChange();
  }, [onQueryChange]);

  useEffect(
    () => () => {
      dispatch(PaginateActions.reset());
    },
    [dispatch]
  );

  return (
    <S.MainPanel>
      <FormPageHeader
        title="Docas Físicas"
        icon={<S.DockIcon />}
        isLoading={loading}
        actions={
          <S.Button
            size="small"
            onClick={() => creationModalRef.current?.openModal()}
          >
            <S.PlusIcon /> Nova Doca Operacional
          </S.Button>
        }
      />
      <PhysicalDockCreationModal
        ref={creationModalRef}
        warehouseId={warehouseId}
        onCreate={onQueryChange}
      />
      <PhysicalDockUpdateModal ref={updateModalRef} onUpdate={onQueryChange} />
      <ConfirmationDialog ref={confirmationDialogRef} />
      <S.FiltersContainer>
        <Form onSubmit={() => {}}>
          <CompactSelect
            name="buildingId"
            placeholder="Prédio"
            options={buildingOptions}
            onChange={onBuildingChange}
            isClearable
          />
        </Form>
      </S.FiltersContainer>
      <PhysicalDocksList
        docks={visibleDocks}
        loading={loading}
        errorMessage={errorMessage}
        selectedDockIds={selectedDockIds}
        onToggleDock={onToggleDock}
        onToggleVisible={onToggleVisible}
        onSelectDock={(id) => updateModalRef.current?.selectDock(id)}
      />
      <Paginator onPageChange={onPageChange} pagination={localPagination} />
      {selectedDockIds.size > 0 && (
        <>
          <S.BulkActionsSpacer />
          <S.BulkActionsBar role="region" aria-label="Ações em massa">
            <S.BulkActionsTitle>
              Ação em massa ({selectedDockIds.size})
            </S.BulkActionsTitle>
            <S.BulkDivider />
            <S.Button
              mood="danger"
              disabled={bulkLoading}
              onClick={onDeleteSelected}
            >
              {bulkLoading ? 'Processando...' : 'Excluir selecionadas'}
            </S.Button>
            <S.CancelBulkButton
              disabled={bulkLoading}
              onClick={() => setSelectedDockIds(new Set())}
            >
              Cancelar seleção
            </S.CancelBulkButton>
          </S.BulkActionsBar>
        </>
      )}
    </S.MainPanel>
  );
};
